import { defineComponent as _defineComponent } from 'vue'
import { ref as _ref, computed as _computed } from 'vue';

import { onMounted, watch } from 'vue';
import type { Location } from 'vue-router';
import { getModule } from 'vuex-module-decorators';
import { useRouter } from 'vue-router/composables';
import {
  DagAssetOverview,
  DashboardAssetOverview,
  DatasetAssetOverview,
  AssetService,
  TransformationAssetOverview, UserUiDto,
  MetadataJobsService,
  DescriptionPredictionFeedbackDto,
  PredictionFeedbackDto,
  AssetDetailsDto,
  LineageEntityDto,
  type AccessTokenProviderDto,
  type FieldDto,
  type DeclaredAssetDetailsDto,
  type AuthorizedAssetHeaderDto,
  type UnauthorizedAssetHeaderDto,
  type AssetDataProductDto,
  DataProductService,
} from '@/api';
import formatDate from '@/utils/filters/formatDate';
import SAvatars from '@/components/SAvatars/SAvatars.vue';
import Tags from '@/components/tags/Tags.vue';
import Terms from '@/components/terms/Terms.vue';
import fromNow from '@/utils/filters/fromNow';
import authModule, { canWithAssetLevelPermission, type AssetPermissionEntityType } from '@/store/modules/auth';
import store from '@/store';
import SRichTextViewer from '@/components/SRichTextViewer.vue';
import SqlDialog from '@/components/sql-dialog/Sql-Dialog.vue';
import ModalDialog from '@/components/modal-dialog/Modal-Dialog.vue';
import i18n from '@/i18n';
import EditDialog from '@/components/generic-edit-dialog/GenericEditDialog.vue';
import { getProviderIconSrc } from '@/utils/provider-icons';
import copyToClipboard from '@/utils/copyToClipboard';
import SIconComposer from '@/components/SIconComposer/SIconComposer.vue';
import CustomMetadataDisplay from '@/components/custom-metadata/CustomMetadataDisplay.vue';
import { type GenericEntity } from '@/components/generic-entity/@types/GenericEntityTypes';
import DataProductsDrawer from '@/modules/data-products/DataProductsDrawer.vue';
import SEllipsisTooltip from '@/components/SEllipsisTooltip/SEllipsisTooltip.vue';
import SModal from '@/components/SModal.vue';
import { usePlans, PlanTypes } from '@/composables/plans';
import DataUsageExtended from '@/components/usage/Usage-Extended.vue';

interface DeleteItem {
  id: string;
  name: string;
  entityKey: string;
}

interface AssetOverviewDetailProps {
  urn: string;
  overviewData: DagAssetOverview | DashboardAssetOverview | DatasetAssetOverview | TransformationAssetOverview | null;
  assetData: AuthorizedAssetHeaderDto | UnauthorizedAssetHeaderDto | null;
  isSmall?: boolean;
  sourceUri?: string;
  sourceId?: string;
}

type AssetOverviewDetailEmits = {
  (event: 'update'): void
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Asset-Overview-Detail',
  props: {
  urn: null,
  overviewData: null,
  assetData: null,
  isSmall: { type: Boolean, default: false },
  sourceUri: null,
  sourceId: null
},
  emits: ["update"],
  setup(__props, { emit }) {

const props = __props;
const auth = getModule(authModule, store);
const router = useRouter();
const plans = usePlans();



const EMPTY_STRING_VALUES = ['', '<p><br></p>'];

let isPlanLoaded = _ref(false);
let hasCatalogEditorPermission = _ref(false);
const sqlDialogRef = _ref<SqlDialog | null>(null);
const modalDialogRef = _ref<ModalDialog | null>(null);
const editDialogRef = _ref<InstanceType<typeof EditDialog> | null>(null);
let isMetadataGenerationEnabled = _ref<boolean>(true);
let customEditPredictionDialogTitle = _ref('');
let selectedDataProduct = _ref<GenericEntity | null>(null);
const deleteModal = _ref<InstanceType<typeof SModal> | null>(null);
let itemToDelete = _ref<DeleteItem>({} as DeleteItem);

const canHaveSuggestions = _computed(() => hasCatalogEditorPermission.value);

const descriptionPrediction = _computed(() => {
  const prediction = props.overviewData?.details?.descriptionPrediction;
  return prediction && prediction.validationStatus === DescriptionPredictionFeedbackDto.validationStatus.NO_FEEDBACK
    ? prediction
    : null;
});

const assetOverview = _computed(() => props.overviewData || {});
const assetDetails = _computed(() => assetOverview.value.details as Partial<DeclaredAssetDetailsDto> || {});
const predictionEditDescription = _computed(() => descriptionPrediction.value?.description ?? '');
const actionable = _computed(() => props.overviewData?.details?.actionable);
const canEdit = _computed(() => hasCatalogEditorPermission.value);
const details = _computed(() => props.overviewData?.details);
const orchestratorsDetails = _computed(() => assetDetails.value?.orchestratorsDetails || []);
const lastAccessDate = _computed(() => (details.value?.usage?.lastAccessDate ? fromNow(details.value?.usage?.lastAccessDate) : '-'));
const usage = _computed(() => details.value?.usage);
const tagsValue = _computed(() => details.value?.tags ?? []);
const termsValue = _computed(() => details.value?.terms ?? []);
const domainsValue = _computed(() => details.value?.domains ?? []);
const dataProductsValue = _computed(() => details.value?.dataProducts ?? []);
const ownersValue = _computed(() => details.value?.owners ?? []);
const descriptionValue = _computed(() => (details.value?.description && !EMPTY_STRING_VALUES.includes(details.value.description)
    ? details.value.description
    : '\\-'));
const isEmptyDescription = _computed(() => descriptionValue.value === '\\-');
const isSystemAdmin = _computed(() => auth.getUserRole === UserUiDto.role.ADMIN);
const isSystemEditor = _computed(() => auth.getUserRole === UserUiDto.role.SYSTEM_EDITOR);
const canPreview = _computed(() => hasCatalogEditorPermission.value);
const externalLinks = _computed(() => (props.assetData && 'externalLinks' in props.assetData ? props.assetData.externalLinks ?? [] : []));
const editDescriptionPredictionTooltip = _computed(() => (!canEdit.value ? i18n.t('app.rights.suggestions_no_rights') : null));
const isDeclaredAsset = _computed(() => props.assetData?.entityType === LineageEntityDto.entityType.DECLARED_ASSET);
const isDeclaredSource = _computed(() => assetDetails.value.sourceCreationType === AssetDetailsDto.sourceCreationType.DECLARED);
const createdBy = _computed(() => assetDetails.value.createdBy as AccessTokenProviderDto);
const lastModifiedBy = _computed(() => assetDetails.value?.lastModifiedBy as AccessTokenProviderDto);
const hasIntegrationDetails = _computed(() => details.value?.integrationName && details.value?.integrationId);

const hasPreview = _computed(() => {
  // By default, the preview feature is enabled, some clients wants to disable it
  const shouldShowPreview = isPlanLoaded.value ? plans.getDataCatalogPlan.value?.isPreviewEnabled ?? true : true;
  return props.overviewData?.details?.hasPreview && shouldShowPreview;
});

const transformation = _computed(() => {
  const d: any = details.value as any;
  return d?.transformation ?? null;
});

const integrationRoute = _computed<Location>(() => ({
  name: 'sources.source',
  params: { id: details.value?.integrationId || '' },
}));
// Note: this is a Vue 2 permission check for route access.
// When migrating to Vue 3, should be replaced with 'canAccessRoute' from PermissionsService,
// so that route permissions are defined in the router config only.
const hasAccessToIntegrationRoute = _computed(() => auth.userActions['integration.datasource.read']);

const hasTransformation = _computed(() => !!transformation.value);
const hasUsage = _computed(() => usage.value && 'qualification' in usage.value);

const externalDescriptionsValue = _computed(() => {
  if (!props.overviewData) return null;
  return ('externalDescriptions' in props.overviewData.details!)
    ? props.overviewData.details!.externalDescriptions : null;
});

const routeToOverview = _computed<Location>(() => ({
  name: 'data-catalog.asset.overview',
  params: { urn: props.urn },
}));

const routeToDomain = (domainId: string) => ({
  name: 'domains.domain.edit',
  params: { id: domainId },
});

const addDescription = async () => {
  const promise = AssetService.sendPredictionFeedback({
    urn: props.urn,
    requestBody: {
      predictionId: descriptionPrediction.value!.id,
      feedbackStatus: PredictionFeedbackDto.feedbackStatus.VALIDATED,
    },
  });
  await promise;
  emit('update');
};

const dismissDescription = async () => {
  const promise = AssetService.sendPredictionFeedback({
    urn: props.urn,
    requestBody: {
      predictionId: descriptionPrediction.value!.id,
      feedbackStatus: PredictionFeedbackDto.feedbackStatus.REJECTED,
    },
  });
  await promise;
  emit('update');
};

const getMetadataJobSettings = async () => {
  const metadataJobSettings = (await MetadataJobsService.getMetadataJobsSettings());
  isMetadataGenerationEnabled.value = metadataJobSettings.metadataGenerationEnabled!;
};

const openSuggestionDialog = (description: string, title: string) => {
  customEditPredictionDialogTitle.value = title;
  editDialogRef.value!.setEntities([{ description }]);
};

const openDescriptionPredictionEdit = () => {
  openSuggestionDialog(predictionEditDescription.value, i18n.t('data-catalog.nested_schema.prediction_edit_title', { name: props.assetData?.name }));
};

const editDialogUpdateHandler = async ({ patches }: { patches: FieldDto[] }) => {
  await AssetService.patchAsset({
    urn: props.urn,
    requestBody: { description: patches[0].description },
  });
  emit('update');
};

const setSelectedDataProduct = (dataProduct: AssetDataProductDto) => {
  selectedDataProduct.value = {
    id: dataProduct.id,
    name: dataProduct.name,
  } as GenericEntity;
};

const handleCloseDataProductsDrawer = () => {
  selectedDataProduct.value = null;
};

const handleCopyToClipboard = (value: string) => {
  copyToClipboard(value, i18n.t('assets.copied_to_clipboard', { value }));
};

const truncateDatasourceName = (value: string) => (value.length > 40 ? `${value.slice(0, 40)}...` : value);

const handleDeleteDataProduct = (dataProduct: GenericEntity) => {
  itemToDelete.value = {
    id: dataProduct.id,
    name: dataProduct.name,
    entityKey: 'data_products',
  } as DeleteItem;
  deleteModal.value?.openModal();
};

const handleUpdateDataProduct = (dataProduct: GenericEntity) => {
  router.push({
      name: 'data-products.data-product.edit',
      params: {
        id: String(dataProduct.id),
      },
    });
};

const onConfirmDeleteItem = () => {
  if (!itemToDelete.value) return;

  switch (itemToDelete.value.entityKey) {
    case 'data_products':
      DataProductService.deleteDataProduct({ id: itemToDelete.value.id })
        .then(() => {
          handleCloseDataProductsDrawer();
          emit('update');
        });
      break;
    default:
        null;
  }

  itemToDelete.value = {} as DeleteItem;
};

const openPreview = () => modalDialogRef.value?.openDialog();
const closePreview = () => modalDialogRef.value?.closeDialog();

watch(
  () => [props.assetData?.id, props.assetData?.entityType] as const,
  async ([id, entityType]) => {
    if (!id || !entityType) {
      hasCatalogEditorPermission.value = false;
      return;
    }
    hasCatalogEditorPermission.value = await canWithAssetLevelPermission('Catalog editor', entityType as AssetPermissionEntityType, id);
    if (hasCatalogEditorPermission.value) {
      await getMetadataJobSettings();
    }
  },
  { immediate: true },
);

onMounted(async () => {
  await plans.refreshPlan(PlanTypes.DATA_CATALOG);
  isPlanLoaded.value = true;
});

return { __sfc: true,auth, router, plans, emit, props, EMPTY_STRING_VALUES, isPlanLoaded, hasCatalogEditorPermission, sqlDialogRef, modalDialogRef, editDialogRef, isMetadataGenerationEnabled, customEditPredictionDialogTitle, selectedDataProduct, deleteModal, itemToDelete, canHaveSuggestions, descriptionPrediction, assetOverview, assetDetails, predictionEditDescription, actionable, canEdit, details, orchestratorsDetails, lastAccessDate, usage, tagsValue, termsValue, domainsValue, dataProductsValue, ownersValue, descriptionValue, isEmptyDescription, isSystemAdmin, isSystemEditor, canPreview, externalLinks, editDescriptionPredictionTooltip, isDeclaredAsset, isDeclaredSource, createdBy, lastModifiedBy, hasIntegrationDetails, hasPreview, transformation, integrationRoute, hasAccessToIntegrationRoute, hasTransformation, hasUsage, externalDescriptionsValue, routeToOverview, routeToDomain, addDescription, dismissDescription, getMetadataJobSettings, openSuggestionDialog, openDescriptionPredictionEdit, editDialogUpdateHandler, setSelectedDataProduct, handleCloseDataProductsDrawer, handleCopyToClipboard, truncateDatasourceName, handleDeleteDataProduct, handleUpdateDataProduct, onConfirmDeleteItem, openPreview, closePreview, _ref, _computed, onMounted, watch, getModule, useRouter, DagAssetOverview, DashboardAssetOverview, DatasetAssetOverview, AssetService, TransformationAssetOverview, UserUiDto, MetadataJobsService, DescriptionPredictionFeedbackDto, PredictionFeedbackDto, AssetDetailsDto, LineageEntityDto, DataProductService, formatDate, SAvatars, Tags, Terms, fromNow, authModule, canWithAssetLevelPermission, store, SRichTextViewer, SqlDialog, ModalDialog, i18n, EditDialog, getProviderIconSrc, copyToClipboard, SIconComposer, CustomMetadataDisplay, DataProductsDrawer, SEllipsisTooltip, SModal, usePlans, PlanTypes, DataUsageExtended }
}

})